import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { StorageService } from "@/persistence/StorageService";
import { Hub } from "./Hub";
import { StoryEditor } from "./StoryEditor";
import { CharacterEditor } from "./CharacterEditor";
import { subscribe, navigate, parseHash, type Route } from "@/router";
import { mountGameInto, type MountedGame } from "@/game/mount";

export function App(): JSX.Element {
  const storage = useMemo(() => new StorageService(), []);
  const [route, setRoute] = useState<Route>(() =>
    parseHash(window.location.hash)
  );

  useEffect(() => subscribe(setRoute), []);

  switch (route.name) {
    case "play":
      return <PlayView storyId={route.params.id ?? ""} storage={storage} />;
    case "story-edit": {
      const id = route.params.id ?? "";
      return <StoryEditor key={id} id={id} />;
    }
    case "character-edit": {
      const id = route.params.id ?? "";
      return <CharacterEditor key={id} id={id} />;
    }
    case "home":
    default:
      return <Hub />;
  }
}

function PlayView({
  storyId,
  storage,
}: {
  storyId: string;
  storage: StorageService;
}): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted: MountedGame | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);

    if (!rootRef.current) return;

    const el = rootRef.current;
    mountGameInto(el, storyId, storage, () => navigate("/"))
      .then((mountedGame) => {
        if (cancelled) {
          mountedGame.dispose();
          return;
        }
        mounted = mountedGame;
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (mounted) {
        mounted.dispose();
        mounted = null;
      }
    };
  }, [storyId, storage]);

  if (error) {
    return (
      <div className="game-error">
        <p>{error}</p>
        <button className="choice-button" onClick={() => navigate("/")}>
          ← Back to the hub
        </button>
      </div>
    );
  }

  return (
    <div className="game-page">
      <div ref={rootRef} className="game-root" />
      {loading ? (
        <p className="game-loading muted">Booting the terminal…</p>
      ) : null}
    </div>
  );
}
